import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { postApi } from "../services/api/api";
import { storeData } from "../services/localStorageService";
import { fetchFcmToken } from "../services/firebaseConfig";

const LoginModal = () => {
    const navigate = useNavigate();
    const modalRef = useRef(null); // Ref to track the modal container

    // Flow State: 'login', 'otp', 'forgot', or 'forgot_success'
    const [step, setStep] = useState("login");

    // NEW STATE: Toggle Password Visibility
    const [showPassword, setShowPassword] = useState(false);

    // Login State
    const [loginData, setLoginData] = useState({
        email: "",
        password: "",
        firebase_token: ""
    });

    // Dedicated standalone state for the forgot password input field
    const [forgotEmail, setForgotEmail] = useState("");

    // Dynamic State to retain mobile number returned by login API
    const [userMobile, setUserMobile] = useState("");

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // OTP State (4 Digits)
    const [otp, setOtp] = useState(["", "", "", ""]);
    const otpRefs = useRef([]);

    // Timer & Resend Counter States
    const [timer, setTimer] = useState(170);
    const [resendCount, setResendCount] = useState(0);
    const timerRef = useRef(null);

    // Reset Form Function
    const resetForm = () => {
        setStep("login");
        setShowPassword(false); // Reset eye toggle state on close
        setLoginData({
            email: "",
            password: "",
            firebase_token: ""
        });
        setForgotEmail(""); // Clear forgot password email state
        setUserMobile(""); // Clear mobile on exit
        setErrors({});
        setOtp(["", "", "", ""]);
        setTimer(170);
        setResendCount(0);
        setLoading(false);
        if (timerRef.current) clearInterval(timerRef.current);
    };

    // Combined Effect to handle token generation on Open AND state cleanup on Close
    useEffect(() => {
        const modalElement = modalRef.current;

        const handleModalShow = async () => {
            const token = await fetchFcmToken();
            console.log('login_firebase_token', token);
            if (token) {
                setLoginData(prev => ({
                    ...prev,
                    firebase_token: token
                }));
            } else {
                setLoginData(prev => ({
                    ...prev,
                    firebase_token: "browser_notification_blocked_or_failed"
                }));
            }
        };

        const handleModalHidden = () => {
            resetForm();
        };

        if (modalElement) {
            modalElement.addEventListener("show.bs.modal", handleModalShow);
            modalElement.addEventListener("hidden.bs.modal", handleModalHidden);
        }

        return () => {
            if (modalElement) {
                modalElement.removeEventListener("show.bs.modal", handleModalShow);
                modalElement.removeEventListener("hidden.bs.modal", handleModalHidden);
            }
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    // Countdown Timer Logic
    useEffect(() => {
        if (step === "otp" && timer > 0) {
            timerRef.current = setInterval(() => {
                setTimer((prevTime) => prevTime - 1);
            }, 1000);
        } else if (timer === 0) {
            clearInterval(timerRef.current);
        }

        return () => clearInterval(timerRef.current);
    }, [step, timer]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setLoginData({ ...loginData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const handleOtpChange = (value, index) => {
        if (!/^[0-9]?$/.test(value)) return;
        const updatedOtp = [...otp];
        updatedOtp[index] = value;
        setOtp(updatedOtp);

        if (value && index < 3) {
            otpRefs.current[index + 1].focus();
        }
    };

    const handleOtpKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpRefs.current[index - 1].focus();
        }
    };

    const validateLogin = () => {
        let tempErrors = {};
        const emailRegex = /\S+@\S+\.\S+/;
        if (!loginData.email) tempErrors.email = "Email is required";
        else if (!emailRegex.test(loginData.email)) tempErrors.email = "Email is invalid";

        if (!loginData.password) tempErrors.password = "Password is required";
        else if (loginData.password.length < 8) tempErrors.password = "Min 8 characters required";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    // FIXED: Strict client-side parameter checks for the forgot password feature
    const validateForgotPassword = () => {
        let tempErrors = {};
        const emailRegex = /\S+@\S+\.\S+/;

        if (!forgotEmail.trim()) {
            tempErrors.forgotEmail = "Email address is required to locate your account.";
        } else if (!emailRegex.test(forgotEmail)) {
            tempErrors.forgotEmail = "Please enter a valid structured email address (e.g. name@domain.com).";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    // STEP 1: Handle login credentials submission
    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        if (!validateLogin()) return;

        setLoading(true);
        try {
            const response = await postApi('login', loginData);
            if (response.code === 200 || response.status === 200 || response.success) {
                const mobileFromResponse = response.data?.mobile || response.mobile || "";
                setUserMobile(mobileFromResponse);

                setStep("otp");
                setTimer(170);
                setResendCount(0);
                setErrors({});
            } else {
                setErrors({ server: response.message || "Invalid Credentials" });
            }
        } catch (error) {
            setErrors({ server: error.response?.data?.message || "Login failed. Try again." });
        } finally {
            setLoading(false);
        }
    };

    // STEP 2: Handle OTP Verification using the mobile number
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const fullOtp = otp.join("");
        if (fullOtp.length < 4) {
            setErrors({ otp: "Please enter 4-digit OTP" });
            return;
        }

        setLoading(true);
        try {
            const response = await postApi('verify-otp', {
                mobile: userMobile,
                otp: fullOtp,
                firebase_token: loginData.firebase_token
            });

            if (response.code === 200 || response.success) {
                storeData('token', response.data.token);
                storeData('userDetails', JSON.stringify(response.data.user_details));
                storeData('profile_data', JSON.stringify(response.data.profile_data));
                const modalElement = modalRef.current;
                const modalInstance = window.bootstrap?.Modal.getInstance(modalElement);
                if (modalInstance) modalInstance.hide();
                const profiles = response.data?.profile_data || [];
                const isProfileCompleted = profiles.length > 0 && Number(profiles[0].profile_completed) === 1;
                navigate(isProfileCompleted ? "/dashboard" : "/CreateProfile");
            } else {
                setErrors({ otp: response.message || "Invalid OTP" });
            }
        } catch (error) {
            setErrors({ otp: error.response?.data?.message || "Verification failed." });
        } finally {
            setLoading(false);
        }
    };

    // STEP 3: Handle Resend OTP using the mobile number
    const handleResendOtp = async () => {
        if (resendCount >= 3) return;

        setLoading(true);
        try {
            const response = await postApi('send-otp', { mobile: userMobile });
            if (response.code === 200 || response.success) {
                setOtp(["", "", "", ""]);
                setTimer(170);
                setResendCount((prev) => prev + 1);
                setErrors({});
                if (otpRefs.current[0]) otpRefs.current[0].focus();
            } else {
                setErrors({ otp: response.message || "Failed to resend OTP." });
            }
        } catch (error) {
            setErrors({ otp: error.response?.data?.message || "Resend failed. Try again." });
        } finally {
            setLoading(false);
        }
    };

    // FIXED: Asynchronous endpoint dispatch handler for password reset queries
    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        if (!validateForgotPassword()) return;

        setLoading(true);
        setErrors({});

        try {
            const response = await postApi('forgot-password', { email: forgotEmail });

            if (response.code === 200 || response.status === 200 || response.success) {
                // If account lookup passes, move to the dedicated feedback screen view step
                setStep("forgot_success");
            } else {
                setErrors({ forgotServer: response.message || "We could not find an account linked to that email." });
            }
        } catch (error) {
            setErrors({ forgotServer: error.response?.data?.message || "Network transaction failed. Please verify connection metrics and retry." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div ref={modalRef} className="modal fade" id="loginModal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="loginModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                        <img src={`${process.env.PUBLIC_URL}/assets/imgs/close_icon.png`} alt="close" />
                    </button>

                    <div className="modal-body p-0">
                        <div className="row">
                            <div className="col-md-5">
                                <div className="login_regi_left">
                                    <h4>Now</h4>
                                    <h1>Find your life partner</h1>
                                    <h4>Easy and fast.</h4>
                                    <div className="log-bg"></div>
                                </div>
                            </div>
                            <div className="col-md-7 center_c">
                                <div className="right_content">

                                    {/* STATE A: LOGIN ENTRY FORM VIEWPORT */}
                                    {step === "login" && (
                                        <div className="login_d">
                                            <div className="log_re_heading">
                                                <h2>Sign in to Matrimony</h2>
                                                <p>Not a member? <a data-bs-toggle="modal" data-bs-target="#registrationModal" style={{cursor: 'pointer'}}>Sign up now</a></p>
                                            </div>

                                            {errors.server && <div className="alert alert-danger p-2 small">{errors.server}</div>}

                                            <form onSubmit={handleLoginSubmit}>
                                                <div className="inputcontainer">
                                                    <h5 className="input_label">Email<span className="m_feild">*</span></h5>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        className={`form-control input_style ${errors.email ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Email"
                                                        value={loginData.email}
                                                        onChange={handleChange}
                                                    />
                                                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                                </div>

                                                <div className="inputcontainer">
                                                    <h5 className="input_label">Password<span className="m_feild">*</span></h5>
                                                    <div className="position-relative">
                                                        <input
                                                            type={showPassword ? "text" : "password"}
                                                            name="password"
                                                            className={`form-control input_style ${errors.password ? 'is-invalid' : ''}`}
                                                            placeholder="Enter Password"
                                                            value={loginData.password}
                                                            onChange={handleChange}
                                                            style={{ paddingRight: "45px" }}
                                                        />
                                                        <span
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            style={{
                                                                position: "absolute",
                                                                right: "15px",
                                                                top: "50%",
                                                                transform: "translateY(-50%)",
                                                                cursor: "pointer",
                                                                zIndex: 10,
                                                                color: "#666",
                                                                fontSize: "16px"
                                                            }}
                                                        >
                                                            <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                                                        </span>
                                                    </div>
                                                    {errors.password && <div className="text-danger small mt-1" style={{ fontSize: "0.875em" }}>{errors.password}</div>}
                                                </div>

                                                <div className="d-flex justify-content-between inputcontainer">
                                                    <div className="form-check m-0"></div>
                                                    {/* FIXED: Bound toggle operation onto the forgot link node element */}
                                                    <a
                                                        href="#!"
                                                        className="forgot_pass_link"
                                                        onClick={(e) => { e.preventDefault(); setStep("forgot"); setErrors({}); }}
                                                    >
                                                        Forgot Password ?
                                                    </a>
                                                </div>

                                                <button type="submit" className="btn next-btn w-100" disabled={loading}>
                                                    {loading ? "Checking..." : "Sign in"}
                                                </button>
                                            </form>
                                        </div>
                                    )}

                                    {/* STATE B: TWO-FACTOR VERIFY OTP FORM VIEWPORT */}
                                    {step === "otp" && (
                                        <div className="verify_otp">
                                            <div className="log_re_heading">
                                                <h2>Verify OTP</h2>
                                                <p>We have sent a code to mobile number ending in <strong>{userMobile ? `******${userMobile.slice(-4)}` : loginData.email}</strong>.</p>
                                            </div>

                                            {errors.otp && <div className="alert alert-danger p-2 small">{errors.otp}</div>}

                                            <form onSubmit={handleVerifyOtp}>
                                                <div className="inputcontainer">
                                                    <h5 className="input_label">Enter OTP<span className="m_feild">*</span></h5>
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        {otp.map((digit, index) => (
                                                            <input
                                                                key={index}
                                                                type="text"
                                                                inputMode="numeric"
                                                                maxLength="1"
                                                                value={digit}
                                                                ref={(el) => (otpRefs.current[index] = el)}
                                                                onChange={(e) => handleOtpChange(e.target.value, index)}
                                                                onKeyDown={(e) => handleOtpKeyDown(e, index)}
                                                                className="form-control otp_input"
                                                                style={{ textAlign: 'center' }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="inputcontainer text-center">
                                                    {timer > 0 ? (
                                                        <p className="otp_ex_txt">Resend OTP after <strong>{formatTime(timer)}</strong></p>
                                                    ) : (
                                                        <p className="text-danger small">Resend OTP.</p>
                                                    )}
                                                </div>

                                                <button type="submit" className="btn next-btn w-100" disabled={loading || timer === 0}>
                                                    {loading ? "Verifying..." : "Verify & Login"}
                                                </button>

                                                <div className="text-center mt-2">
                                                    {resendCount < 3 ? (
                                                        <button
                                                            type="button"
                                                            className="btn trans_btn w-100 border-0"
                                                            onClick={handleResendOtp}
                                                            disabled={timer > 0 || loading}
                                                        >
                                                            Resend OTP {resendCount > 0 && `(${3 - resendCount} attempts left)`}
                                                        </button>
                                                    ) : (
                                                        <small className="text-muted d-block">
                                                            Maximum resend limit reached (3/3). Please try logging in again later.
                                                        </small>
                                                    )}
                                                </div>

                                                <button type="button" className="btn trans_btn w-100" onClick={() => setStep("login")}>
                                                    Back to Login
                                                </button>
                                            </form>
                                        </div>
                                    )}

                                    {/* FIXED STATE C: FORGOT PASSWORD ACTIVE FORM VIEWPORT */}
                                    {step === "forgot" && (
                                        <div className="forgot_password">
                                            <div className="log_re_heading">
                                                <h2>Forgot Password</h2>
                                                <p>Enter your registered email address to reset your password.</p>
                                            </div>

                                            {errors.forgotServer && <div className="alert alert-danger p-2 small">{errors.forgotServer}</div>}

                                            <form onSubmit={handleForgotPasswordSubmit}>
                                                <div className="inputcontainer">
                                                    <h5 className="input_label">Email Address <span className="m_feild">*</span></h5>
                                                    <input
                                                        type="email"
                                                        className={`form-control input_style ${errors.forgotEmail ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Registered Email"
                                                        value={forgotEmail}
                                                        onChange={(e) => {
                                                            setForgotEmail(e.target.value);
                                                            if (errors.forgotEmail) setErrors(prev => ({ ...prev, forgotEmail: "" }));
                                                        }}
                                                    />
                                                    {errors.forgotEmail && <div className="text-danger small mt-1" style={{ fontSize: "0.875em" }}>{errors.forgotEmail}</div>}
                                                </div>

                                                <button type="submit" className="btn next-btn w-100" disabled={loading}>
                                                    {loading ? "Processing..." : "Send Reset Link"}
                                                </button>

                                                <button type="button" className="btn trans_btn w-100 mt-2" onClick={() => { setStep("login"); setErrors({}); }}>
                                                    Back to Login
                                                </button>
                                            </form>
                                        </div>
                                    )}

                                    {/* FIXED STATE D: FORGOT PASSWORD SUCCESS CONFIRMATION PANEL VIEWPORT */}
                                    {step === "forgot_success" && (
                                        <div className="forgot_password">
                                            <div className="success-container text-center py-4">
                                                <div className="success-icon-container mb-3 d-flex align-items-center justify-content-center mx-auto" style={{ width: "60px", height: "60px", borderRadius: "50%", backgroundColor: "#d4edda", color: "#28a745", fontSize: "28px", fontWeight: "bold" }}>
                                                    ✓
                                                </div>

                                                <h2 className="success-title fw-bold text-success mb-2">
                                                    Link Sent Successfully
                                                </h2>

                                                <p className="success-subtitle text-muted mb-4 px-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                                                    We have dispatched a temporary password generation token to <strong>{forgotEmail}</strong>. Please check your inbox and spam folders to finish updating your credentials.
                                                </p>

                                                <button className="btn next-btn w-100" onClick={() => { setStep("login"); setForgotEmail(""); setErrors({}); }}>
                                                    Back to Login
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginModal;